import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormApiService } from '@app/core/services/form-api.service';
import {
  ServiceRequestService, UserResponse, ChargeInformationDto,
  ChargeInformationService, ResponseReasonType
} from '@app/api/dmc';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { AlertService } from '@app/core/services/alert.service';
import { TranslatePipe } from '@ngx-translate/core';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { ToggleComponent } from '@shared/components/input/toggle/toggle.component';
import { catchError, forkJoin, Observable, of, Subscription } from 'rxjs';
import { AppConfigService } from '@app/core/config/app-config.service';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { ServiceRequestCode } from '@app/api/dmc/model/serviceRequestCode';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

type ActionType =
  | 'disconnect'
  | 'disconnectDueToEndOfSupply'
  | 'reconnect'
  | 'closeDown'
  | 'serviceRequestAction';

@Component({
  selector: 'app-dashboard-query-complete',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    InputGroupComponent,
    InputDirective,
    SelectDirective,
    LabelDirective,
    ToggleComponent,
  ],
  templateUrl: './dashboard-query-complete.component.html',
  styleUrl: './dashboard-query-complete.component.css'
})
export class DashboardQueryCompleteComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public form: FormGroup = new FormGroup({
    address: new FormControl(null),
    serviceRequestId: new FormControl(null, Validators.required),
    meteringPointIdentification: new FormControl(null, Validators.required),
    date: new FormControl(this.getTodayDateString(), Validators.required),
    response: new FormControl(null, Validators.required),
    responseReasonType: new FormControl(null),
    reasonText: new FormControl(null),
    isCancelledBySupplier: new FormControl(false),
    chargeInformationId: new FormControl(null),
    forceReconnect: new FormControl(false)
  });

  public chargeInformations: ChargeInformationDto[] = [];

  public element?: any;
  public actionType?: ActionType;

  public responseReasonOptions: KeyValue[] = enumToKeyValue(ResponseReasonType, {
    omitKeys: ['NotSpecified'] as any,
    prefixValue: 'enum.servicerequest.responsereasontype.'
  });

  protected readonly UserResponse = UserResponse;
  protected readonly environment = inject(AppConfigService).settings;

  private responseSub?: Subscription;

  constructor(
    private readonly formApiService: FormApiService,
    private readonly serviceRequestService: ServiceRequestService,
    private readonly alertService: AlertService,
    private readonly chargeInformationService: ChargeInformationService,
  ) {
  }

  public ngOnInit(): void {
    this.initializeForm();
    this.setupAlerts();

    if (this.element?.address) {
      this.form.get('address')?.setValue(this.element.address);
    }
  }

  public ngOnDestroy(): void {
    this.responseSub?.unsubscribe();
    this.alertService.close('modal');
  }

  private getTodayDateString(): string {
    return new Date().toISOString().substring(0, 10);
  }

  private initializeForm(): void {
    this.setActionType();

    if (!this.element) return;

    this.setServiceRequestId();
    this.setupChargeInformations();
    this.setMeteringPointIdentification();
    this.setCancelledBySupplierFlag();
    this.updateDateValidation();
  }

  private setActionType(): void {
    this.actionType = this.actionType ?? this.element?.actionType ?? 'serviceRequestAction';
  }

  private setupChargeInformations(): void {
    const serviceRequestCode = this.element.serviceRequestCode ?? this.getDefaultServiceRequestCode();

    const chargeInfo$: Observable<any> = this.chargeInformationService.chargeInformationQueryPost({
      chargeType: "Fee",
      dataHubStatus: "Confirmed",
      latest: true,
      page: 1,
      pageSize: 20,
      validFrom: new Date().toISOString().split('T')[0]
    }).pipe(
      catchError(error => {
        console.error('Error fetching charge information:', error);
        return of({ results: [] });
      })
    );

    const standardChargeInfo$: Observable<any> = serviceRequestCode
      ? this.serviceRequestService.serviceRequestGetStandardChargeInformationIdForServiceRequestCodeGet(serviceRequestCode).pipe(
        catchError(error => {
          console.error('Error fetching default charge information ID:', error);
          return of(null);
        })
      )
      : of(null);

    forkJoin([chargeInfo$, standardChargeInfo$]).subscribe(([chargeInfoResponse, standardChargeInformationId]) => {
      this.chargeInformations = chargeInfoResponse.results ?? [];
      const chargeInformationControl = this.form.get('chargeInformationId');
      if (standardChargeInformationId) {
        const existsInList = this.chargeInformations?.some(ci => ci.id === standardChargeInformationId);
        chargeInformationControl?.setValue(existsInList ? standardChargeInformationId : null);
      } else {
        // No standard charge information returned; ensure control is null
        chargeInformationControl?.setValue(null);
      }
    });
  }

  private setServiceRequestId(): void {
    const serviceRequestId = this.element.serviceRequestId ?? this.element.businessTransactionId ?? this.element.id;
    if (serviceRequestId) {
      this.form.get('serviceRequestId')?.setValue(serviceRequestId);
    }
  }

  private setMeteringPointIdentification(): void {
    if (this.element.meteringPointIdentification) {
      this.form.get('meteringPointIdentification')?.setValue(this.element.meteringPointIdentification);
    }
  }

  private setCancelledBySupplierFlag(): void {
    if (typeof this.element.isCancelledBySupplier === 'boolean') {
      this.form.get('isCancelledBySupplier')?.setValue(this.element.isCancelledBySupplier);
    }
  }

  private updateDateValidation(): void {
    const dateControl = this.form.get('date');

    if (!dateControl) return;

    if (this.element?.isCancelledBySupplier) {
      // Remove required validator if cancelled by supplier
      dateControl.clearValidators();
      dateControl.updateValueAndValidity();
    } else {
      // Ensure required validator is set
      dateControl.setValidators(Validators.required);
      dateControl.updateValueAndValidity();
    }
  }

  private setupAlerts(): void {
    if (this.shouldShowCancelledBySupplierWarning()) {
      this.showCancelledBySupplierWarning();
      return;
    }

    this.setupResponseChangeAlerts();
  }

  private shouldShowCancelledBySupplierWarning(): boolean {
    return this.element?.isCancelledBySupplier === true;
  }

  private showCancelledBySupplierWarning(): void {
    this.alertService.open('modal', 'info', 'dashboard.servicerequest.cancelledbysupplierwarning');
  }

  private setupResponseChangeAlerts(): void {
    const responseCtrl = this.form.get('response');

    if (!responseCtrl) return;

    this.updateAlert(responseCtrl.value);
    this.responseSub = responseCtrl.valueChanges.subscribe(val => this.updateAlert(val));
  }

  private updateAlert(response: UserResponse | null): void {
    const messages = this.getAlertMessages(response);

    if (messages.length > 0) {
      const alertType = this.getAlertType(response);
      this.alertService.open('modal', alertType, messages);
    } else {
      this.alertService.close('modal');
    }
  }

  private getAlertType(response: UserResponse | null): 'info' | 'danger' {
    return response === UserResponse.Rejected ? 'danger' : 'info';
  }

  private getAlertMessages(response: UserResponse | null): string[] {
    if (!response) return [];

    if (response === UserResponse.Rejected) {
      return this.getRejectedMessages();
    }

    if (response === UserResponse.Approved) {
      return this.getApprovedMessages();
    }

    return [];
  }

  private getRejectedMessages(): string[] {
    if (this.actionType === 'disconnectDueToEndOfSupply') {
      return ['dashboard.servicerequest.disconnectDueToEndOfSupply.completereject'];
    }

    return ['dashboard.servicerequest.complete.reject'];
  }

  private getApprovedMessages(): string[] {
    return [
      `dashboard.servicerequest.${this.actionType}.completeapprove`,
      `dashboard.servicerequest.${this.actionType}.completeinfo`
    ];
  }

  private getDefaultServiceRequestCode(): ServiceRequestCode | undefined {
    switch (this.actionType) {
      case 'disconnect':
        return ServiceRequestCode.Disconnect;
      case 'reconnect':
        return ServiceRequestCode.Connect;
      case 'closeDown':
        return ServiceRequestCode.CloseDown;
      case 'disconnectDueToEndOfSupply':
        return ServiceRequestCode.DisconnectDueToEndOfSupply;
      default:
        return undefined;
    }
  }

  public submit(): void {
    const apiCall = this.getApiCall();

    this.formApiService.submit(
      this.form,
      apiCall,
      {
        closeModal: true,
        successMessage: 'dashboard.servicerequest.reply.success'
      }
    );
  }

  private getApiCall() {
    const apiCallMap: Record<ActionType, (data: any) => any> = {
      reconnect: (data) => this.serviceRequestService.serviceRequestReconnectMeteringPointActionRequestPost(data),
      disconnect: (data) => this.serviceRequestService.serviceRequestDisconnectMeteringPointActionRequestPost(data),
      disconnectDueToEndOfSupply: (data) => this.serviceRequestService.serviceRequestSubmitUserRequestPost(data),
      closeDown: (data) => this.serviceRequestService.serviceRequestCloseDownMeteringPointActionRequestPost(data),
      serviceRequestAction: (data) => this.serviceRequestService.serviceRequestSubmitActionRequestPost(data),
    };

    return apiCallMap[this.actionType ?? 'serviceRequestAction'];
  }
}
